import React, { useCallback } from "react";
import { useField } from "formik";
import { useCategoriesById } from "@hooks/useCategoriesById";
import { useEntitiesById } from "@hooks/useEntities";
import {
    useWorkspaceAccountGroups,
    useWorkspaceAccounts,
    useWorkspaceAccrualAccounts,
} from "@api/account.api";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import {
    RuleGroupType,
    TransferRuleDirection,
} from "@common/types/transactionRule";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import {
    TransactionRuleConditionFormSchema,
    TransactionRuleActionFormSchema,
    FormRuleActionType,
} from "../constants";
import { getCategoryVisibility } from "../useEntityCategoryVisibility";
import { getFieldNamePrefix } from "../../utils";
import {
    clearInvalidCategoryActionForEntitySelection,
    clearInvalidCategoryConditionsForEntitySelection,
} from "./entityConditionHelpers";
import { clearInvalidAccountActionForEntitySelection } from "./accountActionEntityHelpers";

interface Props {
    index: number;
    groupType: RuleGroupType;
    transferDirection?: TransferRuleDirection;
    container?: HTMLElement;
}

export const EntityConditionForm: React.FC<Props> = ({
    index,
    groupType,
    transferDirection,
    container,
}) => {
    const categoriesById = useCategoriesById();
    const entitiesById = useEntitiesById({ excludeMockEntities: true });
    const accounts = useWorkspaceAccounts();
    const accrualAccounts = useWorkspaceAccrualAccounts();
    const accountGroups = useWorkspaceAccountGroups();
    const showAccountNumbers = useShowAccountNumbers();
    const fieldName = `${getFieldNamePrefix(index, transferDirection)}.entityValue`;
    const [field, , helpers] =
        useField<TransactionRuleConditionFormSchema["entityValue"]>(fieldName);

    const [actions, , actionsHelpers] =
        useField<TransactionRuleActionFormSchema[]>("actions");

    const conditionsFieldName = transferDirection
        ? `conditions.${transferDirection}`
        : "conditions";
    const [conditions, , conditionsHelpers] =
        useField<TransactionRuleConditionFormSchema[]>(conditionsFieldName);

    const onChange = useCallback(
        (entityIds: number[] | undefined) => {
            const entityAction = actions.value.find(
                (action) => action.actionType === FormRuleActionType.ENTITY,
            );

            const visibilityArgs = {
                entityIds: entityIds ?? [],
                entityActionEntityId: entityAction?.value?.entityId,
                entitiesById,
            };

            const actionsVisibility = getCategoryVisibility({
                ...visibilityArgs,
                context: "actions",
            });

            const conditionsVisibility = getCategoryVisibility({
                ...visibilityArgs,
                context: "conditions",
            });
            const nextConditions =
                clearInvalidCategoryConditionsForEntitySelection({
                    conditions: conditions.value,
                    categoriesById,
                    visibility: conditionsVisibility,
                });

            if (nextConditions !== conditions.value) {
                conditionsHelpers.setValue(nextConditions);
            }

            let nextActions = clearInvalidCategoryActionForEntitySelection({
                actions: actions.value,
                categoriesById,
                visibility: actionsVisibility,
            });

            const moveToEntityId = entityAction?.value?.entityId;
            const accountTargetEntityIds =
                moveToEntityId !== undefined ? [moveToEntityId] : entityIds;

            nextActions = clearInvalidAccountActionForEntitySelection({
                actions: nextActions,
                entityIds: accountTargetEntityIds,
                accounts: accounts.data,
                accrualAccounts: accrualAccounts.data,
                groupsByEntityId: accountGroups.data,
                showAccountNumbers,
            });

            if (nextActions !== actions.value) {
                actionsHelpers.setValue(nextActions);
            }

            helpers.setValue(entityIds);
        },
        [
            actions.value,
            helpers,
            categoriesById,
            entitiesById,
            actionsHelpers,
            accounts.data,
            accrualAccounts.data,
            accountGroups.data,
            showAccountNumbers,
            conditions.value,
            conditionsHelpers,
        ],
    );

    return (
        <EntityFilterCombobox
            filters={{ entityIds: field.value ?? [] }}
            onChange={(filters) => onChange(filters.entityIds)}
            onlyBusiness={groupType === RuleGroupType.ACCOUNTING}
            container={container}
            excludeAllMockEntities
            mutuallyExclusive
        />
    );
};
